/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { NextRequest } from "next/server";

import { getBestLocaleFromRequest } from "./locale";

const EULA_REFERENCE_PATH = "eula-reference";
const LOCALE_SUFFIX_REGEX = /^-([a-zA-Z]{2}(-[a-zA-Z]{2})?)$/;

/**
 * Splits a compound eula slug into its first URL segment and the remainder.
 *
 * @param eulaSlug - Eula page path, simple or compound (e.g., `"eula"`, `"eula/content"`).
 * @returns `basePath` — first segment matched in the URL; `subPath` — everything after the first `/`, or `""`.
 *
 * @example
 * parseEulaSlug("eula")          // { basePath: "eula", subPath: "" }
 * parseEulaSlug("eula/content")  // { basePath: "eula", subPath: "content" }
 * parseEulaSlug("eula/foo/bar")  // { basePath: "eula", subPath: "foo/bar" }
 */
const parseEulaSlug = (eulaSlug: string): { basePath: string; subPath: string } => {
	const slashIndex = eulaSlug.indexOf("/");
	if (slashIndex === -1) return { basePath: eulaSlug, subPath: "" };
	return {
		basePath: eulaSlug.slice(0, slashIndex),
		subPath: eulaSlug.slice(slashIndex + 1),
	};
};

/**
 * Issues a 302 redirect to `pathname`, preserving the original query string and host.
 *
 * @param request - The incoming Next.js request whose `nextUrl` is mutated in-place.
 * @param pathname - Absolute pathname for the redirect target (e.g., `"/eula"`).
 * @returns A 302 `Response` with the `Location` header set.
 */
const redirect = (request: NextRequest, pathname: string): Response => {
	const url = request.nextUrl.clone();
	url.pathname = pathname;
	return Response.redirect(url);
};

/** Builds a localized EULA reference slug without adding a URL locale prefix. */
const buildEulaRefPath = (locale: string, eulaReferenceSlug: string): string =>
	`/${EULA_REFERENCE_PATH}/${eulaReferenceSlug}-${locale.toLowerCase()}`;

/**
 * Returns `true` when the path segments following the eula base slug are a valid
 * match for the expected eula page (including sub-paths and extra trailing segments).
 *
 * Matches when:
 * - `restSegments` is empty — the URL ends exactly at the eula base (e.g., `/ja/eula`).
 * - First segment equals `eulaReferenceSlug` — legacy redirect-target form.
 * - Joined rest equals `eulaSubPath` or starts with `eulaSubPath/` — handles compound slugs
 *   like `eula/content` and tolerates extra trailing segments (e.g., `/ja/eula/content/extra`).
 *
 * @param restSegments - URL segments after the eula base path segment.
 * @param eulaReferenceSlug - Product identifier (e.g., `"content"`, `"twinmotion"`).
 * @param eulaSubPath - Sub-path portion of the eula slug (e.g., `"content"` from `"eula/content"`), or `""`.
 * @returns `true` if the path should trigger a redirect, `false` to pass through.
 */
const eulaPathMatches = (restSegments: string[], eulaReferenceSlug: string, eulaSubPath: string): boolean => {
	const first = restSegments[0];
	if (!first) return true;
	if (first === eulaReferenceSlug) return true;
	if (!eulaSubPath) return false;
	const rest = restSegments.join("/");
	return rest === eulaSubPath || rest.startsWith(`${eulaSubPath}/`);
};

/** Redirects EULA references to the slug for the language resolved from the request. */
function resolveEulaRefRedirect(
	request: NextRequest,
	locale: string,
	defaultLocale: string,
	eulaReferenceSlug: string,
	restPath: string,
	defaultEulaPath: string,
): Response | void {
	if (!restPath.startsWith(eulaReferenceSlug)) return;

	const suffix = restPath.slice(eulaReferenceSlug.length);
	if (suffix !== "" && !LOCALE_SUFFIX_REGEX.test(suffix)) {
		return;
	}

	const refLocale = suffix ? suffix.slice(1) : null;

	if (locale === defaultLocale) {
		return redirect(request, defaultEulaPath);
	}

	if (refLocale && locale.toLowerCase() === refLocale.toLowerCase()) {
		return;
	}

	return redirect(request, buildEulaRefPath(locale, eulaReferenceSlug));
}

/**
 * Enforces canonical EULA URLs without locale prefixes.
 * Resolves language from `?lang`, EPIC_LOCALE, Accept-Language, or the default.
 * Legacy locale-prefixed paths are left for processLocale to redirect first.
 * Supports compound page slugs such as "eula/content".
 */
export function processEulaReference(
	request: NextRequest,
	locales: string[],
	defaultLocale: string,
	eulaReferenceSlug: string,
	eulaSlug: string = "eula",
	unsupportedLocaleMapping?: Record<string, string>,
): Response | void {
	const { pathname } = request.nextUrl;
	const pathnameHasLocale = locales.some((loc) => pathname.startsWith(`/${loc}/`) || pathname === `/${loc}`);
	if (pathnameHasLocale) return; // processLocale will strip the prefix first; ignore until path is clean

	const segments = pathname.split("/");
	const slug = segments[1];
	if (!slug) return;

	const { basePath: eulaBasePath, subPath: eulaSubPath } = parseEulaSlug(eulaSlug);

	// Early exit: not an eula or eula-reference path
	const isEulaPath = slug === eulaBasePath;
	const isEulaRefPath = slug === EULA_REFERENCE_PATH;
	if (!isEulaPath && !isEulaRefPath) return;

	const locale = getBestLocaleFromRequest(request, locales, defaultLocale, unsupportedLocaleMapping);
	const rest = segments.slice(2); // no locale segment — slug is at [1], rest starts at [2]

	if (isEulaRefPath) {
		return resolveEulaRefRedirect(request, locale, defaultLocale, eulaReferenceSlug, rest.join("/"), `/${eulaSlug}`);
	}

	// Default locale eula page serves directly — no redirect to eula-reference
	if (locale === defaultLocale) {
		return;
	}

	// Sub-path doesn't match this eula slug — pass through
	if (!eulaPathMatches(rest, eulaReferenceSlug, eulaSubPath)) {
		return;
	}

	return redirect(request, buildEulaRefPath(locale, eulaReferenceSlug));
}
